import { WELCOME_ONBOARDING_STORAGE_PREFIX } from '../constants/welcome-onboarding.constants'
import type { MemberOnboardingResponse, WelcomeOnboardingVisitState, WelcomeWidgetRenderMode } from '../types/types'

export type { WelcomeWidgetRenderMode }

export function buildWelcomeVisitStorageKey(locationId: string, groupId: string, userId: string): string {
  return `${WELCOME_ONBOARDING_STORAGE_PREFIX}:${locationId}:${groupId}:${userId}`
}

export function defaultWelcomeVisitState(): WelcomeOnboardingVisitState {
  return {
    hasVisited: false,
    completedSeenAt: null,
  }
}

/** True when `expiresAt` is present and already in the past (invalid dates are ignored). */
export function isWelcomeOnboardingExpired(expiresAt?: string | null, nowMs: number = Date.now()): boolean {
  if (!expiresAt) return false
  const ms = Date.parse(expiresAt)
  if (Number.isNaN(ms)) return false
  return ms <= nowMs
}

/**
 * Member board is live for optimistic AUTO ticks / session memory.
 * BE history-sync sets `eligible: false` (and may omit tasks) when the window closed;
 * `expiresAt` is a FE safety net if the GET is briefly stale.
 */
export function isWelcomeMemberOnboardingActive(
  data: Pick<MemberOnboardingResponse, 'eligible' | 'expiresAt' | 'tasks'> | null | undefined,
  nowMs: number = Date.now()
): boolean {
  if (!data?.eligible) return false
  if (!data.tasks?.length) return false
  if (isWelcomeOnboardingExpired(data.expiresAt, nowMs)) return false
  return true
}

export function resolveWelcomeWidgetRenderMode(input: {
  eligible: boolean
  checklistComplete: boolean
  taskCount: number
  visit: WelcomeOnboardingVisitState
  expiresAt?: string | null
  nowMs?: number
}): WelcomeWidgetRenderMode {
  if (!input.eligible || input.taskCount === 0 || isWelcomeOnboardingExpired(input.expiresAt, input.nowMs)) {
    return 'hidden'
  }

  if (input.checklistComplete) {
    return input.visit.completedSeenAt ? 'hidden' : 'all_complete'
  }

  return input.visit.hasVisited ? 'collapsed' : 'expanded'
}
